/**
 * The start-workout flow's decisions, kept out of its rendering.
 *
 * The endpoint reports facts and nothing else, so every frame the flow can
 * draw is derived here — first match wins, in the order below — and each
 * platform only lays out what this returns. Shared by the web sheet and the
 * mobile card so the two can never disagree about what to offer.
 *
 * Decisions and numbers live here; the sentences that carry them live in
 * `start-copy.ts`, where a reviewer can read them in one place.
 */

import { dayjs, formatDisplayDate } from '../../date';
import {
  MUSCLE_LABELS,
  muscleMapModelFromIds,
  preferredMuscleView,
} from '../../muscle-map';
import { toSentenceList } from '../../text';

import type { ID } from '../../../types/common/id.types';
import type {
  StartOptionsAvoid,
  StartOptionsCompletedToday,
  StartOptionsDay,
  StartOptionsDayStatus,
  StartOptionsMissed,
  StartOptionsSuggestion,
  StartOptionsWorkout,
  StartWorkoutOptions,
} from '../../../types/domain/start-options.types';
import type { MuscleFace, MuscleId, MuscleMapModel } from '../../muscle-map';

// =============================================================================
// THE FRAMES
// =============================================================================

/**
 * Which of the flow's two steps is showing. The picker is reached from every
 * main frame through the escape hatch, and left through the header's back arrow.
 */
export type StartWorkoutStep = 'main' | 'picker';

/** Why the picker is standing in for a main frame. */
export type PickerReason =
  /** Too little history for the coverage metric to judge. */
  | 'not_enough_data'
  /** The analytics behind both suggestion sources are above this tier. */
  | 'not_entitled'
  /** Reached from a main frame's escape hatch. */
  | 'chosen';

export type StartWorkoutFrame =
  /** Nothing to show yet, and nothing remembered. */
  | { kind: 'loading' }
  /** The request failed with no remembered answer to fall back to. */
  | { kind: 'error'; message: string }
  /** No workouts exist. The one frame whose CTA leaves the flow. */
  | { kind: 'empty' }
  /** Today's session is due. */
  | { kind: 'scheduled'; pick: StartOptionsWorkout }
  /** Today's session is logged. */
  | { kind: 'done'; session: StartOptionsCompletedToday }
  /** A session was logged today that the schedule did not ask for. */
  | { kind: 'extra'; session: StartOptionsCompletedToday }
  /** Nothing due today, but a session earlier this week was never started. */
  | { kind: 'catchup'; missed: StartOptionsMissed }
  /** An active schedule with no entry today. */
  | { kind: 'rest' }
  /** An active plan that pins nothing to dates — the plan recommends. */
  | { kind: 'plan_next'; picks: StartOptionsWorkout[] }
  /** No plan, and the analytics had something to say. */
  | { kind: 'suggestions'; picks: StartOptionsSuggestion[] }
  /** The picker, opened directly rather than through a hatch. */
  | { kind: 'picker'; reason: PickerReason };

/**
 * Read the payload into a frame.
 *
 * The order is the design's ladder and is load-bearing: `done` must beat
 * `extra`, or a completed plan session would report itself as an extra one; and
 * `extra` must beat `catchup`, or the flow would push Monday's workout at
 * someone who has just finished it.
 */
export function deriveFrame(
  options: StartWorkoutOptions | null,
  error: string | null
): StartWorkoutFrame {
  // A remembered answer outranks a failure — the hook only reports an `error`
  // when it had nothing to fall back on, so the two are never both present.
  if (!options)
    return error ? { kind: 'error', message: error } : { kind: 'loading' };

  if (options.workouts.length === 0) return { kind: 'empty' };

  const { plan, scheduled } = options;

  if (scheduled && !scheduled.completed_at) {
    return { kind: 'scheduled', pick: scheduled.workout };
  }

  if (scheduled?.completed_at) {
    const session =
      options.completed_today.find((entry) => entry.on_schedule) ??
      options.completed_today[0];
    if (session) return { kind: 'done', session };
  }

  if (plan?.scheduled) {
    const session = options.completed_today[options.completed_today.length - 1];
    if (session) return { kind: 'extra', session };

    const missed = options.missed[0];
    if (missed) return { kind: 'catchup', missed };

    return { kind: 'rest' };
  }

  if (plan) return { kind: 'plan_next', picks: planOrder(options) };

  if (options.suggestions.length > 0) {
    return { kind: 'suggestions', picks: options.suggestions };
  }

  return {
    kind: 'picker',
    reason:
      options.suggestions_status === 'not_entitled'
        ? 'not_entitled'
        : options.suggestions_status === 'not_enough_data'
          ? 'not_enough_data'
          : 'chosen',
  };
}

/**
 * The plan's own workouts, longest-neglected first — the recommendation an
 * unscheduled plan makes for itself. Never-done workouts lead, because a
 * workout the plan holds and the user has never opened is the longest gap
 * there is.
 */
export function planOrder(options: StartWorkoutOptions): StartOptionsWorkout[] {
  return options.workouts
    .filter((workout) => workout.plan !== null)
    .slice()
    .sort((a, b) => daysSince(b) - daysSince(a));
}

/** Days since a workout was last done; `Infinity` when never. */
function daysSince(workout: StartOptionsWorkout): number {
  if (!workout.last_completed_at) return Number.POSITIVE_INFINITY;
  return dayjs().diff(dayjs(workout.last_completed_at), 'day');
}

/** Which workout each frame arrives with already selected. */
export function defaultSelection(frame: StartWorkoutFrame): ID | null {
  switch (frame.kind) {
    case 'scheduled':
      return frame.pick.id;
    case 'catchup':
      return frame.missed.workout.id;
    case 'plan_next':
      return frame.picks[0]?.id ?? null;
    case 'suggestions':
      return frame.picks[0]?.workout.id ?? null;
    default:
      return null;
  }
}

/**
 * The key a selection is seeded under. When it changes — a fresh answer
 * landing, or the flow reopening onto a different day — the selection
 * re-seeds to the frame's own default, so no frame is ever drawn with the
 * previous one's pick.
 */
export function selectionSeedKey(frame: StartWorkoutFrame): string {
  return `${frame.kind}:${defaultSelection(frame) ?? ''}`;
}

/** Whether the picker is on screen: opened through a hatch, or standing in. */
export function isPickerShowing(
  frame: StartWorkoutFrame,
  step: StartWorkoutStep
): boolean {
  return step === 'picker' || frame.kind === 'picker';
}

// =============================================================================
// THE COMMIT ZONE
// =============================================================================

/**
 * What the commit zone holds.
 *
 * - `skeleton` — a placeholder the height of the CTA, while it waits.
 * - `none` — a cold failure; the body carries the retry.
 * - `create` — the empty library's one way forward.
 * - `start` — the CTA alone (the picker is already the whole library).
 * - `choose` — an outline into the picker, for the frames with nothing to start.
 * - `start_and_hatch` — the CTA, with the whole library one tap beneath it.
 */
export type StartFooterKind =
  'skeleton' | 'none' | 'create' | 'start' | 'choose' | 'start_and_hatch';

export function footerKind(
  frame: StartWorkoutFrame,
  step: StartWorkoutStep
): StartFooterKind {
  if (frame.kind === 'loading') return 'skeleton';
  if (frame.kind === 'error') return 'none';
  if (frame.kind === 'empty') return 'create';
  if (isPickerShowing(frame, step)) return 'start';
  if (
    frame.kind === 'done' ||
    frame.kind === 'extra' ||
    frame.kind === 'rest'
  ) {
    return 'choose';
  }
  return 'start_and_hatch';
}

// =============================================================================
// THE PICKS
// =============================================================================

/**
 * A frame's picks with the selected one promoted into the card and the rest
 * beneath it — so whatever the CTA names always has its justification on
 * screen. Falls back to the first pick when the selection is not among them.
 */
export function promote<T>(
  picks: readonly T[],
  selectedId: ID | null,
  idOf: (pick: T) => ID
): { chosen: T; rest: T[] } | null {
  const chosen =
    picks.find((pick) => idOf(pick) === selectedId) ?? picks[0] ?? null;
  if (chosen === null) return null;

  return {
    chosen,
    rest: picks.filter((pick) => idOf(pick) !== idOf(chosen)),
  };
}

// =============================================================================
// THE FIGURE
// =============================================================================

/**
 * The figure's model and the face to open on.
 *
 * Built from ids the API has already resolved, NOT through the free-text alias
 * table — that table matches by substring and would read `lower_back` as the
 * lats. Same face rule as the exercise page: the primary mover decides.
 */
export function figureOf(workout: StartOptionsWorkout): {
  model: MuscleMapModel;
  face: MuscleFace;
} {
  const model = muscleMapModelFromIds(workout.muscles);
  return { model, face: preferredMuscleView(model) };
}

// =============================================================================
// LABELS AND NUMBERS
// =============================================================================

/**
 * Muscle names as prose, in the app's own vocabulary rather than the API's.
 *
 * Lowercased because these land mid-sentence; the map's chips print the same
 * names capitalised. One vocabulary across both, so a muscle never appears to
 * be renamed by the surface it is read on.
 */
export function muscleNames(ids: readonly MuscleId[]): string {
  return toSentenceList(ids.map((id) => MUSCLE_LABELS[id].toLowerCase()));
}

/** Sentence-cased for the head of a line: "Lats", "Hamstrings and calves". */
export function leadingMuscleNames(ids: readonly MuscleId[]): string {
  const [first, ...rest] = ids;
  if (!first) return '';
  return toSentenceList([
    MUSCLE_LABELS[first],
    ...rest.map((id) => MUSCLE_LABELS[id].toLowerCase()),
  ]);
}

/**
 * The typical length of a session, in whole minutes — the mean of what has
 * actually been logged. Null until the workout has been done once, and never
 * rounded up from nothing.
 */
export function typicalMinutes(workout: StartOptionsWorkout): number | null {
  if (workout.avg_duration_seconds === null) return null;
  return Math.max(1, Math.round(workout.avg_duration_seconds / 60));
}

/** "a day ago" · "5 days ago" · "9 Sept 2026" · "not done yet". */
export function lastDoneLabel(workout: StartOptionsWorkout): string {
  return (
    formatDisplayDate(workout.last_completed_at, 'recent') ?? 'not done yet'
  );
}

/**
 * The muscles a pick works only lightly that the avoid list names.
 *
 * The endpoint filters picks on their PRIMARY muscles alone — a half-credit
 * signal has no half-exclusion — so a workout whose assisting muscles are sore
 * is still offered. Rather than second-guess that, the card says it out loud.
 * Every input is already in the payload; this is copy, not transport.
 */
export function assistingCaution(
  workout: StartOptionsWorkout,
  avoid: StartOptionsAvoid
): { sore: MuscleId[]; trainedHard: MuscleId[] } {
  const assisting = workout.muscles.secondary;

  return {
    sore: assisting.filter((id) => avoid.sore_today.includes(id)),
    trainedHard: assisting.filter((id) => avoid.trained_hard.includes(id)),
  };
}

/** Hours since a muscle's last credited set, as whole days. */
export function daysFromHours(hours: number): number {
  return Math.max(0, Math.round(hours / 24));
}

/** A logged session's length in whole minutes, never zero. */
export function sessionMinutes(session: StartOptionsCompletedToday): number {
  return Math.max(1, Math.round(session.duration_seconds / 60));
}

// =============================================================================
// THE WEEK
// =============================================================================

/**
 * The status a day's dot draws. An extra session happened, so the day reads as
 * filled even if nothing was due.
 */
export function ribbonDotStatus(day: StartOptionsDay): StartOptionsDayStatus {
  if (day.extra > 0 && (day.status === 'rest' || day.status === 'due')) {
    return 'done';
  }
  return day.status;
}

/** The week's counts, for the figures beside the ribbon. */
export function weekTally(days: readonly StartOptionsDay[]): {
  done: number;
  planned: number;
  extra: number;
  missed: number;
} {
  return {
    done: days.filter((day) => day.status === 'done').length,
    planned: days.filter((day) => day.status !== 'rest').length,
    extra: days.reduce((total, day) => total + day.extra, 0),
    missed: days.filter((day) => day.status === 'missed').length,
  };
}

// =============================================================================
// THE PICKER
// =============================================================================

/** Below this the picker groups are short enough to read without searching. */
export const SEARCH_FROM = 12;

export interface PickerGroup {
  key: string;
  label: string;
  /** True for a group that stands alone rather than beside a plan. */
  solo: boolean;
  workouts: StartOptionsWorkout[];
}

/**
 * The picker's rows.
 *
 * Plan members see their plan first and everything else by recency; everyone
 * else sees the three recency bands. A query collapses both into one list of
 * matches, because a search result that stayed grouped would hide its own hits.
 */
export function pickerGroups(
  options: StartWorkoutOptions,
  query: string
): PickerGroup[] {
  const all = options.workouts;
  const term = query.trim().toLowerCase();

  if (term) {
    const hits = byRecency(
      all.filter((workout) => workout.name.toLowerCase().includes(term))
    );
    return [
      {
        key: 'matches',
        label: `${hits.length} ${hits.length === 1 ? 'match' : 'matches'}`,
        solo: true,
        workouts: hits,
      },
    ];
  }

  if (options.plan) {
    const inPlan = all.filter((workout) => workout.plan !== null);
    const outside = byRecency(all.filter((workout) => workout.plan === null));

    return trim([
      {
        key: 'plan',
        label: `Your plan · ${options.plan.name}`,
        solo: false,
        workouts: inPlan,
      },
      {
        key: 'outside',
        label: 'Not in your plan',
        solo: true,
        workouts: outside,
      },
    ]);
  }

  const sorted = byRecency(all);
  const week = sorted.filter((workout) => daysSince(workout) <= 7);
  const earlier = sorted.filter((workout) => {
    const days = daysSince(workout);
    return days > 7 && Number.isFinite(days);
  });
  const never = sorted.filter((workout) => !workout.last_completed_at);

  return trim([
    { key: 'week', label: 'Last 7 days', solo: true, workouts: week },
    { key: 'earlier', label: 'Earlier', solo: true, workouts: earlier },
    { key: 'never', label: 'Not done yet', solo: true, workouts: never },
  ]);
}

/** Whether the picker tops its list with a search field. */
export function showsPickerSearch(options: StartWorkoutOptions): boolean {
  return options.workouts.length >= SEARCH_FROM;
}

/** Most recently done first, never-done last. */
function byRecency(workouts: StartOptionsWorkout[]): StartOptionsWorkout[] {
  return workouts.slice().sort((a, b) => daysSince(a) - daysSince(b));
}

function trim(groups: PickerGroup[]): PickerGroup[] {
  return groups.filter((group) => group.workouts.length > 0);
}

/** Look a workout up by id across everything the payload carries. */
export function findWorkout(
  options: StartWorkoutOptions,
  id: ID | null
): StartOptionsWorkout | null {
  if (!id) return null;
  return options.workouts.find((workout) => workout.id === id) ?? null;
}
